import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { BookMarked, Search } from "lucide-react";
import type { CannedResponse } from "@shared/schema";
import { substituteMessageVars } from "@/lib/message-placeholders";

type CannedResponseWithMeta = CannedResponse & { updatedByName?: string };

interface CannedResponsePickerProps {
  // Task #2120 — `cannedId` is passed alongside the substituted text so the
  // consumer can tag the insertion and only record usage when the containing
  // message is actually sent (instead of the picker firing record-use on
  // insert, which inflated counts when the host deleted the text or never
  // sent the message).
  onInsert: (text: string, cannedId: number) => void;
  memberFirstName?: string;
  memberFullName?: string;
  propertyName?: string | null;
  propertyFullAddress?: string | null;
  propertyShortAddress?: string | null;
  roomNumber?: string | null;
  memberMoveInDate?: string | Date | null;
  memberLeaseEndDate?: string | Date | null;
  weeklyRent?: string | number | null;
  hostName?: string | null;
  disabled?: boolean;
}

export function CannedResponsePicker({
  onInsert,
  memberFirstName,
  memberFullName,
  propertyName,
  propertyFullAddress,
  propertyShortAddress,
  roomNumber,
  memberMoveInDate,
  memberLeaseEndDate,
  weeklyRent,
  hostName,
  disabled,
}: CannedResponsePickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const query = useQuery<CannedResponseWithMeta[]>({
    queryKey: ["/api/app/canned-responses"],
  });
  // Task #2121 — pull the per-owner category metadata so the picker honors
  // any pinned sort order the team configured in the Manage Categories modal.
  const categoryMetaQuery = useQuery<Array<{ name: string; count: number; sortOrder: number | null }>>({
    queryKey: ["/api/app/canned-responses/categories"],
  });
  const sortOrderMap = new Map<string, number>();
  for (const c of categoryMetaQuery.data ?? []) {
    if (c.sortOrder !== null) sortOrderMap.set(c.name, c.sortOrder);
  }

  const responses = query.data ?? [];
  const filtered = search.trim()
    ? responses.filter(
        (r) =>
          String(r.title ?? "").toLowerCase().includes(search.toLowerCase()) ||
          String(r.body ?? "").toLowerCase().includes(search.toLowerCase()) ||
          String(r.category ?? "").toLowerCase().includes(search.toLowerCase())
      )
    : responses;

  // Group filtered responses by category. `null` / empty becomes "Uncategorized"
  // and is always rendered last so labeled groups float to the top.
  const UNCATEGORIZED = "Uncategorized";
  const groups = new Map<string, CannedResponseWithMeta[]>();
  for (const r of filtered) {
    const key = r.category && r.category.trim() ? r.category.trim() : UNCATEGORIZED;
    const arr = groups.get(key);
    if (arr) arr.push(r);
    else groups.set(key, [r]);
  }
  // Task #2121 — sort: explicit sortOrder first (in numeric order), then any
  // remaining categories alphabetically, with "Uncategorized" always last.
  const groupEntries = Array.from(groups.entries()).sort(([a], [b]) => {
    if (a === UNCATEGORIZED) return 1;
    if (b === UNCATEGORIZED) return -1;
    const aOrder = sortOrderMap.get(a);
    const bOrder = sortOrderMap.get(b);
    if (aOrder !== undefined && bOrder !== undefined) return aOrder - bOrder || a.localeCompare(b);
    if (aOrder !== undefined) return -1;
    if (bOrder !== undefined) return 1;
    return a.localeCompare(b);
  });

  function handlePick(r: CannedResponseWithMeta) {
    const text = substituteMessageVars(r.body, { memberFirstName, memberFullName, propertyName, propertyFullAddress, roomNumber, memberMoveInDate, memberLeaseEndDate, weeklyRent, hostName });
    // Task #2120 — Hand the canned-response id back to the consumer so it can
    // tag the insertion. Usage is recorded by the send paths after the
    // containing message is actually sent, not at insertion time.
    onInsert(text, r.id);
    setOpen(false);
    setSearch("");
  }

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setSearch(""); }}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon"
          disabled={disabled}
          title="Insert canned response"
          data-testid="button-canned-picker"
          data-tour="messages-canned-picker"
        >
          <BookMarked className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 p-0"
        align="end"
        data-testid="popover-canned-responses"
      >
        <div className="border-b border-gray-100 p-3">
          <p className="text-xs font-semibold text-gray-700 mb-2">Canned Responses</p>
          <div className="relative">
            <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-gray-400" />
            <Input
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 pl-7 text-sm"
              data-testid="input-canned-search"
            />
          </div>
        </div>
        <div className="max-h-72 overflow-y-auto">
          {query.isLoading && (
            <p className="px-3 py-4 text-center text-sm text-gray-400">Loading…</p>
          )}
          {!query.isLoading && filtered.length === 0 && (
            <p className="px-3 py-4 text-center text-sm text-gray-400">
              {responses.length === 0
                ? "No canned responses yet. Add one on the Canned Responses tab."
                : "No matches found."}
            </p>
          )}
          {groupEntries.map(([category, items]) => (
            <div key={category} data-testid={`canned-group-${category}`}>
              <div
                className="sticky top-0 z-10 bg-gray-50 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-100"
                data-testid={`canned-group-header-${category}`}
              >
                {category}
              </div>
              {items.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => handlePick(r)}
                  className="w-full text-left px-3 py-2.5 hover:bg-gray-50 border-b border-gray-50 last:border-0 transition-colors"
                  data-testid={`canned-option-${r.id}`}
                >
                  <p className="text-sm font-medium text-gray-900 truncate">{r.title}</p>
                  <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{r.body}</p>
                </button>
              ))}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
